import { useEffect, useState } from 'react';
import { Box, Typography, Radio } from '@mui/material';
import ReactTable from '../../../components/shared/ReactTable';
import { COLUMNS } from '../../../constants/tableColumns';
import { useGetModemsQuery } from '../../../services/Modems/ModemsService';
import FIELD_TYPE from '../../../constants/fieldType';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { NoContent, PositionedMenu, Datepicker } from '../../../components/shared';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';
import useStyles from './style';
import { getDateFormat } from '../../../utils/common-methods';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { NOT_APPLICABLE } = FIELD_TYPE;

const AssignNewModemTable = ({
  onSelect,
  q,
  selectedRows,
  onDeleteModem,
  onDateChange,
  inEffectFromDates,
  assetId,
}) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [searchFilterData, setSearchFilterData] = useState(q);
  const [skipCall, setSkipCall] = useState(false);
  const [tableData, setTableData] = useState([]);
  const classes = useStyles();
  const {
    data: latestData,
    isError,
    isSuccess,
    isLoading,
    error,
  } = useGetModemsQuery(
    {
      searchParams: `q=${searchFilterData}`,
      assetId: assetId,
      page: currentPage,
      perPage: perPageNumber,
    },
    { skip: skipCall },
  );

  const COLUMN_IN_EFFECT_FROM = {
    id: 'inEffectFrom',
    Header: 'IN EFFECT FROM',
    accessor: 'inEffectFrom',
    width: 250,
    Cell: ({ row }) => (
      <Box sx={{ minWidth: '220px' }}>
        {selectedRows[row.original.id] ? (
          <Datepicker
            value={inEffectFromDates[row.original.id] ? dayjs.utc(inEffectFromDates[row.original.id]) : null}
            onChange={(date) => onDateChange(row.original.id, date)}
            error={selectedRows[row.original.id] && !inEffectFromDates[row.original.id]}
            maxDate={new Date()}
            helperText={
              selectedRows[row.original.id] && !inEffectFromDates[row.original.id]
                ? 'In Effect From date is required'
                : ''
            }
            size="small"
            sx={{ minWidth: '200px' }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {row.original?.modemHistories?.inEffectFrom
              ? getDateFormat(row.original?.modemHistories?.inEffectFrom)
              : NOT_APPLICABLE}
          </Typography>
        )}
      </Box>
    ),
  };

  const COLUMN_POSITIONED_MENU = [
    {
      Header: () => null,
      id: 'more',
      Cell: ({ row }) => (
        <Box component="span" className={!row.original.status ? classes.archive : ''}>
          <PositionedMenu
            menuItemData={[
              {
                btnName: 'Delete Modem',
                btnClickFunc: () => {
                  onDeleteModem(row.original);
                },
              },
            ]}
            disabled={selectedRows[row.original.id]}
          />
        </Box>
      ),
    },
  ];

  const MODEMS_COLUMNS = [
    {
      id: 'selection Location',
      Header: () => null,
      width: 50,
      Cell: ({ row }) => (
        <>
          <Radio
            onChange={(e) => onSelect(row.original, e.target.checked)}
            checked={!!selectedRows[row.original.id]}
            sx={{ ml: 2 }}
          />
        </>
      ),
    },
    ...COLUMNS.ASSIGN_MODEM_TABLE,
    COLUMN_IN_EFFECT_FROM,
    ...COLUMN_POSITIONED_MENU,
  ];

  useEffect(() => {
    if (isSuccess && latestData) {
      const data = latestData?.modems?.map((modem) => {
        // Always use 'id' as the unique key for the table
        const id = modem.id || modem.modemId;
        const machineName = modem?.assetId ? modem?.asset?.title || '' : NOT_APPLICABLE;
        const installedDate = modem.installedDate ? getDateFormat(modem.installedDate) : NOT_APPLICABLE;
        return { ...modem, id, assetName: machineName, installedDate: installedDate };
      });
      setTableData(data);
      let total = latestData?.pagination?.total ?? 0;
      setTotalCount(total);
    }
  }, [isSuccess, latestData]);

  useEffect(() => {
    setSkipCall(true);
    setSearchFilterData(q);
    setCurrentPage(DEFAULT_PAGE);
    setSkipCall(false);
  }, [q]);

  const pageChangeHandler = (currentPage) => {
    setCurrentPage(currentPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
  };

  return (
    <Box className={classes.assignModemTableWrapper}>
      {isLoading && <LoadingSpinner containerHeight={300} />}
      {isError && (
        <NoContent
          title="No Modem Found"
          desc={(isError && error?.data?.message) || <>There are no modem present with this filter.</>}
        />
      )}
      {!isLoading && !isError && tableData && (
        <ReactTable
          data={tableData}
          columns={MODEMS_COLUMNS}
          pagination
          manualPagination
          totalCount={totalCount}
          pageNumberForTablePagination={currentPage - 1}
          onPageChange={pageChangeHandler}
          onPerPageChange={perPageChangeHandler}
          perPage={perPageNumber}
          renderRows
        />
      )}
    </Box>
  );
};

export default AssignNewModemTable;
